/**
 * Batch Job Dashboard Page
 * Main monitoring dashboard for all batch jobs — health, summary, and manual triggers
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Timer,
  Activity,
  ChevronDown,
  ChevronRight,
  History,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn, formatDate, formatDuration, formatNumber } from '@/lib/utils'
import { getBatchJobHealth, getBatchJobSummary } from '../queries/batchJobs'
import { JobHealthGrid } from '../components/JobHealthGrid'
import { JobStatusBadge } from '../components/JobStatusBadge'
import { ManualTriggerPanel } from '../components/ManualTriggerPanel'
import type { BatchJobHealthResponse, BatchJobSummaryResponse } from '../types'

function formatSchedule(cron: string | null | undefined): string {
  if (!cron) return 'Manual only'
  const map: Record<string, string> = {
    '0 0 */2 * * *': 'Every 2 hours',
    '0 0 2 * * *': 'Daily 2:00 AM UTC',
    '0 0 5 * * *': 'Daily 5:00 AM UTC',
    '0 */30 * * * *': 'Every 30 min',
  }
  return map[cron] ?? cron
}

export function BatchJobDashboardPage() {
  const [triggersOpen, setTriggersOpen] = useState(false)

  // Health query — auto-refresh every 30s
  const {
    data: health,
    isLoading: healthLoading,
    error: healthError,
    refetch: refetchHealth,
  } = useQuery<BatchJobHealthResponse>({
    queryKey: ['batch-jobs', 'health'],
    queryFn: getBatchJobHealth,
    refetchInterval: 30000,
  })

  // Summary query — auto-refresh every 30s
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery<BatchJobSummaryResponse>({
    queryKey: ['batch-jobs', 'summary'],
    queryFn: getBatchJobSummary,
    refetchInterval: 30000,
  })

  const handleRefresh = () => {
    refetchHealth()
    refetchSummary()
  }

  // Derived stats from summary
  const jobs = summary?.jobs ?? []
  const totalPairs = summary?.totalJobAccountPairs ?? 0
  const failingCount = jobs.filter((j) => j.lastStatus === 'FAILED').length
  const healthyCount = totalPairs - failingCount
  const avgSuccessRate =
    jobs.length > 0
      ? jobs.reduce((sum, j) => sum + (j.successRate7d ?? 0), 0) / jobs.length
      : 0

  const isOverallHealthy = health?.overallStatus === 'HEALTHY'

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Timer className="h-6 w-6 text-indigo-600" />
            Batch Job Monitoring
          </h1>
          <p className="text-gray-500 mt-1">
            Monitor scheduled and manual batch job executions across all accounts.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">Auto-refreshes every 30s</span>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Link to="/batch-jobs/history">
            <Button variant="outline">
              <History className="h-4 w-4 mr-2" />
              View History
            </Button>
          </Link>
        </div>
      </div>

      {/* Overall Health Banner */}
      {health && (
        <div
          className={cn(
            'flex items-center gap-3 rounded-xl border px-5 py-4',
            isOverallHealthy
              ? 'border-green-200 bg-green-50'
              : 'border-red-200 bg-red-50',
          )}
        >
          {isOverallHealthy ? (
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
          ) : (
            <XCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
          )}
          <div>
            <p
              className={cn(
                'font-semibold',
                isOverallHealthy ? 'text-green-800' : 'text-red-800',
              )}
            >
              {isOverallHealthy
                ? 'All Systems Healthy'
                : `Degraded — ${failingCount} job${failingCount !== 1 ? '(s)' : ''} failing`}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Last checked: {formatDate(health.generatedAt)}
            </p>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Total Job/Account Pairs</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatNumber(totalPairs)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Healthy</p>
          <p className="text-2xl font-bold text-green-700 mt-1">
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-5 w-5" />
              {formatNumber(healthyCount)}
            </span>
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Failing</p>
          <p className="text-2xl font-bold text-red-700 mt-1">
            <span className="flex items-center gap-1.5">
              <XCircle className="h-5 w-5" />
              {formatNumber(failingCount)}
            </span>
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Avg Success Rate (7d)</p>
          <p className="text-2xl font-bold text-indigo-700 mt-1">
            {avgSuccessRate.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Job Health Grid */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Activity className="h-5 w-5 text-gray-500" />
          Job Health
        </h2>
        {healthLoading && <LoadingState message="Loading health data..." />}
        {healthError && (
          <ErrorState
            error={healthError as Error}
            message="Failed to load health data"
            onRetry={refetchHealth}
          />
        )}
        {health && <JobHealthGrid health={health} />}
      </section>

      {/* Job Summary Table */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Job Summary (Last 7 Days)</h2>
        {summaryLoading && <LoadingState message="Loading summary..." />}
        {summaryError && (
          <ErrorState
            error={summaryError as Error}
            message="Failed to load summary"
            onRetry={refetchSummary}
          />
        )}
        {summary && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Job</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Account</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Schedule</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Last Run</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Duration</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Rows</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">7d Success</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Last Failure</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {jobs.map((job, idx) => (
                    <tr key={`${job.jobName}-${job.accountId ?? 'all'}-${idx}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        {job.displayName}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {job.accountId != null ? `Account ${job.accountId}` : 'All'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {formatSchedule(job.scheduleExpression)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {job.lastRunAt ? formatDate(job.lastRunAt) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <JobStatusBadge status={job.lastStatus} />
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap font-mono">
                        {formatDuration(job.lastDurationMs)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap font-mono">
                        {job.lastRowsProcessed != null ? formatNumber(job.lastRowsProcessed) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <span
                          className={cn(
                            'font-semibold',
                            job.successRate7d >= 90
                              ? 'text-green-700'
                              : job.successRate7d >= 70
                                ? 'text-yellow-700'
                                : 'text-red-700',
                          )}
                        >
                          {job.successRate7d?.toFixed(0)}%
                        </span>
                        <span className="text-xs text-gray-400 ml-1">
                          ({job.successRuns7d}/{job.totalRuns7d})
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px]">
                        {job.lastFailureAt ? (
                          <div>
                            <div className="text-red-600 whitespace-nowrap">{formatDate(job.lastFailureAt)}</div>
                            {job.lastErrorMessage && (
                              <div className="truncate text-gray-400" title={job.lastErrorMessage}>
                                {job.lastErrorMessage}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {jobs.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                        No job data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Manual Trigger Panel — collapsible */}
      <section>
        <button
          onClick={() => setTriggersOpen((o) => !o)}
          className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-indigo-600 transition-colors"
        >
          {triggersOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Manual Triggers
        </button>
        {triggersOpen && (
          <div className="mt-3">
            <ManualTriggerPanel />
          </div>
        )}
      </section>
    </div>
  )
}
